import { CheckCircle2, Mail, Phone, Star } from "lucide-react";

import { Card } from "@/components/ui/card";
import { LocalDateTime } from "@/components/ui/local-datetime";
import { PACKAGE_LABEL, STATUS_LABEL } from "@/lib/admin-orders-shared";
import { formatPrice } from "@/lib/packages";
import { cn } from "@/lib/utils";

import { StatusPill } from "./status-pill";
import type { OrderDetail } from "./order-header";

export type OrderFeedback = {
  rating: number;
  comment: string | null;
};

export function OrderSidebar({
  order,
  feedback,
}: {
  order: OrderDetail;
  feedback: OrderFeedback | null;
}) {
  return (
    <div className="space-y-4">
      <Card className="space-y-3 p-5">
        <div className="space-y-1">
          <p className="text-[10.5px] uppercase tracking-[0.08em] text-muted-foreground">Customer</p>
          <p className="text-sm font-medium">{order.contactName}</p>
        </div>
        <a
          href={`mailto:${order.contactEmail}`}
          className="flex items-center gap-2 text-sm underline-offset-4 hover:underline"
        >
          <Mail className="size-3.5 text-muted-foreground" />
          {order.contactEmail}
        </a>
        {order.contactPhone ? (
          <a
            href={`tel:${order.contactPhone}`}
            className="flex items-center gap-2 text-sm underline-offset-4 hover:underline"
          >
            <Phone className="size-3.5 text-muted-foreground" />
            {order.contactPhone}
          </a>
        ) : null}
      </Card>

      {(order.customerCompletedAt || feedback) ? (
        <Card className="space-y-3 p-5 text-sm">
          <p className="text-[10.5px] uppercase tracking-[0.08em] text-muted-foreground">Customer signals</p>
          {order.customerCompletedAt ? (
            <div className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="size-4" />
              <span className="text-xs">
                Confirmed delivery{" "}
                <LocalDateTime iso={order.customerCompletedAt} preset="long" />
              </span>
            </div>
          ) : null}
          {feedback ? <FeedbackBlock feedback={feedback} /> : null}
        </Card>
      ) : null}

      <Card className="space-y-3 p-5 text-sm">
        <Row label="Status" value={<StatusPill status={order.status} />} />
        <Row label="Package" value={PACKAGE_LABEL[order.packageType]} />
        <Row label="Price" value={formatPrice(order.priceUsd)} />
        <Row label="Submitted" value={<LocalDateTime iso={order.createdAt} preset="long" />} />
        <Row
          label="Paid"
          value={order.paidAt ? <LocalDateTime iso={order.paidAt} preset="long" /> : <span className="text-muted-foreground">—</span>}
        />
        <Row
          label="Started"
          value={order.startedAt ? <LocalDateTime iso={order.startedAt} preset="long" /> : <span className="text-muted-foreground">—</span>}
        />
        <Row label="Deadline" value={<LocalDateTime iso={order.deadlineAt} preset="long" />} />
        {order.completedAt ? (
          <Row label="Completed" value={<LocalDateTime iso={order.completedAt} preset="long" />} />
        ) : null}
        <Row label="Order" value={<span className="font-mono text-xs">{order.orderNumber}</span>} />
        <Row label="State" value={<span className="text-xs text-muted-foreground">{STATUS_LABEL[order.status]}</span>} />
        {order.stripePaymentIntentId ? (
          <Row
            label="Stripe PI"
            value={<span className="font-mono text-[11px] break-all text-muted-foreground">{order.stripePaymentIntentId}</span>}
          />
        ) : null}
      </Card>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-[10.5px] uppercase tracking-[0.08em] text-muted-foreground">{label}</span>
      <span className="min-w-0 text-right">{value}</span>
    </div>
  );
}

function FeedbackBlock({ feedback }: { feedback: OrderFeedback }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1" role="img" aria-label={`Customer rated ${feedback.rating} out of 5`}>
        {[1, 2, 3, 4, 5].map((n) => (
          <Star
            key={n}
            className={cn("size-4", n <= feedback.rating ? "fill-amber-400 stroke-amber-500" : "fill-transparent stroke-muted-foreground/40")}
            strokeWidth={1.5}
          />
        ))}
        <span className="ml-1 text-xs text-muted-foreground">{feedback.rating}/5</span>
      </div>
      {feedback.comment ? (
        <p className="whitespace-pre-wrap rounded-md border bg-muted/30 px-2.5 py-2 text-xs text-foreground/90">{feedback.comment}</p>
      ) : (
        <p className="text-xs italic text-muted-foreground">No comment.</p>
      )}
    </div>
  );
}
